import React, { useMemo } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartArea,
  ScriptableContext,
  PointElement,
} from 'chart.js'
import { Bar, Chart, Line } from 'react-chartjs-2'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { ContextData } from '@floating-ui/react-dom-interactions'
import { match, P } from 'ts-pattern'
import dayjs from 'dayjs'
import 'chartjs-plugin-crosshair'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
)

const getGreenToLemonGreenGradient = (ctx: ContextData, chartArea: ChartArea) => {
  const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom)
  gradient.addColorStop(0, '#279661')
  gradient.addColorStop(1, '#8ECB5F')
  return gradient
}

const gradients = {
  'green-to-lemon-green': getGreenToLemonGreenGradient,
}

const disableAnimations = false

const options = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index' as const,
    intersect: false,
  },
  scales: {
    y: {
      type: 'linear' as const,
      display: true,
      position: 'left' as const,
    },
    y2: {
      type: 'linear' as const,
      display: true,
      position: 'right' as const,
      grid: {
        drawOnChartArea: false,
      },
    },
  },
  plugins: {
    legend: {
      display: true,
      position: 'bottom' as const,
    },
    tooltip: {
      mode: 'index' as const,
      intersect: false,
    },
  },
}

const ComboChart = ({
  data,
  isLoading = false,
  isPreviousData = false,
  xKey = 'date',
  y1Key,
  y2Key,
}: {
  data: Array<any>
  isLoading: boolean
  isPreviousData: boolean
  xKey: string
  y1Key: string
  y2Key: string
}) => {
  const labels = useMemo(() => {
    return data?.map((d) => d[xKey])
  }, [data, xKey])
  const chartData = {
    labels: labels,
    datasets: [
      {
        type: 'line' as const,
        label: y2Key,
        data: data.map((item) => item[y2Key]),
        borderColor: '#4776E6',
        backgroundColor: '#4776E6',
        yAxisID: 'y2' as const,
        datalabels: {
          display: false,
        },
      },
      {
        type: 'bar' as const,
        label: y1Key,
        data: data.map((item) => item[y1Key]),
        backgroundColor: (context) => {
          const chart = context.chart
          const { ctx, chartArea } = chart
          if (!chartArea) return null
          return gradients['green-to-lemon-green'](ctx, chartArea)
        },
        yAxisID: 'y' as const,
        datalabels: {
          display: false,
        },
      },
    ],
  }

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">Loading...</div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center text-web-gray opacity-50">
        No Data
      </div>
    )
  }

  return (
    <div className="relative h-full w-full">
      {isPreviousData && (
        <div className="absolute top-0 left-0 w-full h-full bg-white opacity-50" />
      )}
      <Chart options={options} data={chartData} type="bar" />
    </div>
  )
}

export default ComboChart
